import React, {
    useCallback, useMemo, useRef, useState,
} from 'react';
import moment from 'moment-timezone';
import MapTripReport from '../../../../component/mapTripReport/mapTripReport';
import Table from '../../../../component/table/Table';
import { IOSSwitch, OutlinedButton } from '../../../../component/buttonComponent/ButtonComponent';
import { MAP_THEMES } from '../../../../config/function/mapStyles';
import {
    CHARGE_COLOR, CHARGE_LABEL, EVENT_COLOR, EVENT_LABEL, FUEL_COLOR, FUEL_LABEL, IDLE_COLOR,
    IDLE_LABEL, PROGRESS_COLOR, ROUTE_COLOR,
} from '../../../../component/mapTripReport/mapColors';
import { OD_TELEMETRY_ENGINES_ENABLED, OD_TRIP_TAPE_ENABLED } from '../../../../config/featureFlags';
import TripRail from '../components/TripRail';
import TripTape from '../components/TripTape';
import odTableStyles from '../components/odTableStyles';
import { safetyColumns } from './SafetyView';
import { idleColumns } from './IdleView';
import {
    buildFocus,
    buildTripNumbers,
    compactDuration,
    toKm,
    UNKNOWN_PLACE,
} from '../odAnalyticsUtils';

/**
 * Readout for the route under the cursor, in the map's top-right corner.
 *
 * Trip-level only. The prototype's panel reads the nearest telemetry *sample* — timestamp, speed,
 * motion state at the hovered point — which needs the per-trip trail this panel does not have yet.
 * What it does show is still what hovering is for: identifying a route without clicking it. The
 * sample rows join this card when the trail lands, for the selected trip.
 *
 * Numbers come from buildTripNumbers so the card and the rail always agree — the server's tripNo
 * indexes the whole requested window and diverges the moment a cross-filter is active.
 */
const HoverPanel = ({ trips, hoverTripId, timezone }) => {
    const numbers = useMemo(() => buildTripNumbers(trips), [trips]);
    const trip = useMemo(
        () => (hoverTripId ? (trips || []).find((candidate) => candidate.startTraceId === hoverTripId) : null),
        [trips, hoverTripId],
    );

    if (!trip) return null;

    const idleRatio = trip.idlingRatio;
    const rows = [
        ['Start', moment.tz(trip.startTime, timezone).format('D MMM, HH:mm:ss')],
        ['End', trip.endTime ? moment.tz(trip.endTime, timezone).format('D MMM, HH:mm:ss') : 'In progress'],
        ['Distance', `${toKm(trip.totalDistance)} km`],
        ['Duration', compactDuration(trip.totalDuration, true)],
        [
            'Idle share',
            idleRatio === null || idleRatio === undefined
                ? '—'
                : `${(Number(idleRatio) * 100).toFixed(1)}%`,
        ],
    ];

    return (
        <div className="od-map-hover" aria-live="polite">
            <div className="od-map-hover-title">
                {`Trip #${numbers.get(trip.startTraceId) || '—'}`}
                {trip.assetName ? <span className="od-map-hover-asset">{trip.assetName}</span> : null}
            </div>
            <div className="od-map-hover-route">
                {`${trip.origin || UNKNOWN_PLACE} → ${trip.destination || UNKNOWN_PLACE}`}
            </div>
            {rows.map(([label, value]) => (
                <div className="od-map-hover-kv" key={label}>
                    <span className="od-map-hover-key">{label}</span>
                    <span className="od-map-hover-value">{value}</span>
                </div>
            ))}
        </div>
    );
};

/**
 * Everything the map can draw, top-right under the hover readout.
 *
 * fleetconsole's `renderLegend`, entry for entry and in its order. It is a fixed key rather than a
 * list of what this window happens to contain: a legend that grows and shrinks as you change the
 * date range is a legend you have to re-read, and an absent row reads as "this map cannot show
 * that" rather than "there were none". Colours come from the same mapColors module the layer paint
 * reads, so the swatch and the dot cannot disagree.
 *
 * Labels compose from the shared EVENT_LABEL / IDLE_LABEL maps so the legend, the Safety Event
 * column and the tape's tooltips keep saying the same words. The suffixes are fleetconsole's and
 * describe the *mark* rather than the event: the overspeed dot sits on the episode's peak sample,
 * and the idle class numbers are the ones the Idle tab's Class column prints.
 */
const MapLegend = ({ mapTheme }) => {
    // Route and travelled are both theme-paired, and they swap ends of the Horizon ramp together —
    // see PROGRESS_COLOR. Reading the same flag for both is what stops the legend inverting the
    // pair on one theme and not the other.
    const satellite = mapTheme === MAP_THEMES.SATELLITE;
    const entries = [
        {
            key: 'route',
            label: 'Trip route',
            swatch: true,
            color: satellite ? ROUTE_COLOR.satellite : ROUTE_COLOR.monochrome,
        },
        {
            key: 'progress',
            label: 'Travelled to scrub head',
            swatch: true,
            color: satellite ? PROGRESS_COLOR.satellite : PROGRESS_COLOR.monochrome,
        },
        { key: 'HARSH_BRAKE', label: EVENT_LABEL.HARSH_BRAKE, color: EVENT_COLOR.HARSH_BRAKE },
        { key: 'HARSH_ACCEL', label: EVENT_LABEL.HARSH_ACCEL, color: EVENT_COLOR.HARSH_ACCEL },
        { key: 'HARSH_CORNER', label: EVENT_LABEL.HARSH_CORNER, color: EVENT_COLOR.HARSH_CORNER },
        { key: 'OVERSPEED', label: `${EVENT_LABEL.OVERSPEED} (peak)`, color: EVENT_COLOR.OVERSPEED },
        { key: 'idle-450', label: `${IDLE_LABEL[450]} 450`, color: IDLE_COLOR[450] },
        { key: 'idle-460', label: `${IDLE_LABEL[460]} 460`, color: IDLE_COLOR[460] },
        // Beyond fleetconsole's own legend, which never listed the ledger events even though its
        // map pins them. Recharge is ours alone — the prototype has a CHARGE type in its ledger
        // filter but never detects or draws one.
        { key: 'REFILL', label: FUEL_LABEL.REFILL, color: FUEL_COLOR.REFILL },
        { key: 'DRAIN', label: FUEL_LABEL.DRAIN, color: FUEL_COLOR.DRAIN },
        { key: 'CHARGE', label: CHARGE_LABEL, color: CHARGE_COLOR },
    ];

    return (
        <div className="od-map-legend">
            {entries.map((entry) => (
                <div className="od-map-legend-row" key={entry.key}>
                    <span
                      className={entry.swatch ? 'od-map-legend-swatch' : 'od-map-legend-dot'}
                      style={{ background: entry.color }}
                    />
                    <span>{entry.label}</span>
                </div>
            ))}
        </div>
    );
};

/** The prototype's deck subset of the Safety tab's columns — see safetyColumns. */
const SAFETY_DECK_COLUMNS = ['start', 'event', 'trip', 'peakKmh', 'duration', 'g', 'location'];

/** The prototype's deck subset of the Idle tab's columns — see idleColumns. */
const IDLE_DECK_COLUMNS = ['start', 'duration', 'classCode', 'excess', 'basis', 'location'];

/**
 * One deck table, scoped to the selected trip.
 *
 * The columns come from the tab views' own exported factories rather than a second set defined
 * here, so a column that changes on the Safety or Idle tab changes here too. The deck shows a
 * subset — no Asset column (one trip is one asset) and none of the tab-only columns.
 *
 * `scopeToTrip` is the prototype's "selected trip only" checkbox. Unchecked, the panel widens to
 * every row in the window, which is what makes it useful before a trip is picked.
 */
const TripScopedPanel = (props) => {
    const {
        title, subtitle, rows, columns, selectedTrip, scopeToTrip, onToggleScope, emptyText, note,
        onRowClicked, tripNo,
    } = props;

    const scoped = useMemo(() => {
        const list = Array.isArray(rows) ? rows : [];
        if (!scopeToTrip || !selectedTrip) return list;
        return list.filter((row) => row.tripId === selectedTrip.startTraceId);
    }, [rows, scopeToTrip, selectedTrip]);

    // Name the trip, as fleetconsole's `evCountMap` does — "in this trip" leaves the reader to
    // check the rail for which one, and the rail scrolls. `tripNo` is undefined only if the
    // selection has been filtered out from under the panel, where the vaguer wording is honest.
    const scopeLabel = tripNo === undefined ? 'in this trip' : `in trip #${tripNo}`;

    return (
        <div className="od-card">
            <div className="od-card-header">
                <div className="od-card-header-text">
                    <div className="od-card-title">
                        {title}
                        <span className="od-badge od-badge-neutral">
                            {scopeToTrip && selectedTrip ? `${scoped.length} ${scopeLabel}` : `${scoped.length} total`}
                        </span>
                    </div>
                    <div className="od-card-subtitle">{subtitle}</div>
                </div>
                <div className="od-card-actions">
                    <div className="od-map-control-row">
                        <span className="od-map-control-text">Selected trip only</span>
                        <IOSSwitch checked={scopeToTrip} onChange={onToggleScope} />
                    </div>
                </div>
            </div>
            {scoped.length === 0 ? (
                <div className="od-empty">
                    <div className="od-empty-text">{emptyText}</div>
                </div>
            ) : (
                <div className="od-table-wrap">
                    <Table
                      columns={columns}
                      tableData={scoped}
                      module="odAnalytics"
                      onRowClicked={onRowClicked}
                      highlightOnHover
                      pointerOnHover
                      customStyles={odTableStyles}
                      fixedHeaderScrollHeight="25rem"
                    />
                </div>
            )}
            {note ? <div className="od-card-note">{note}</div> : null}
        </div>
    );
};

/**
 * The Route Atlas map view: trip rail, map, reserved tape slot, analysis deck.
 */
const RouteMapView = (props) => {
    const {
        trips,
        totalTripCount,
        selectedTrip,
        onSelectTrip,
        hoverTripId,
        onHoverTrip,
        timezone,
        mapTheme,
        onToggleMapTheme,
        showTripNumbers,
        onToggleTripNumbers,
        onResetView,
        canResetView,
        events,
        episodes,
        fuelEvents,
        chargeSessions,
        trail,
        trailLoading,
        focus,
        onClearFocus,
        onFocusEvent,
        onFocusEpisode,
        onFocusFuel,
        onFocusCharge,
    } = props;

    /**
     * A dot clicked on the map → the same readout its table row gives.
     *
     * The map reports which row was hit, not the row itself: a map feature carries only the few
     * properties the paint expressions need, and the popup's wording needs the whole record.
     */
    // `==`, not `===`: mapbox round-trips feature properties through its own tile encoding, and a
    // numeric id can come back as a string.
    const findRow = useCallback(({ kind, id }) => {
        const source = {
            event: events, idle: episodes, fuel: fuelEvents, charge: chargeSessions,
        }[kind];
        // eslint-disable-next-line eqeqeq
        return (source || []).find((candidate) => candidate.id == id) || null;
    }, [events, episodes, fuelEvents, chargeSessions]);

    const onPickPoint = useCallback(({ kind, id }) => {
        const handler = {
            event: onFocusEvent, idle: onFocusEpisode, fuel: onFocusFuel, charge: onFocusCharge,
        }[kind];
        const row = findRow({ kind, id });
        if (row && typeof handler === 'function') handler(row);
    }, [findRow, onFocusEvent, onFocusEpisode, onFocusFuel, onFocusCharge]);

    /**
     * A deck row pins its point on the map — which sits above the deck, off-screen by the time the
     * reader has scrolled down to click. Bring the map to them; the rail scrolls itself to the
     * newly selected trip (see TripRail), so both halves of the answer are in view.
     */
    const mapWrapRef = useRef(null);
    const withMapScroll = useCallback((handler) => (row) => {
        handler(row);
        mapWrapRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, []);

    // Default on, as in the design: the deck exists to explain the trip on the map. Unchecking it
    // widens both panels to the whole window.
    const [scopeToTrip, setScopeToTrip] = useState(true);
    // Tape state lives here, not in ODAnalytics: the cursor changes ~12 times a second, and the
    // container renders the asset picker, the date range and every KPI. RouteMapView is the nearest
    // common parent of the tape and the map, which are the only two things that read it.
    const [cursor, setCursor] = useState(null);
    const [follow, setFollow] = useState(true);

    // Two sources, not one: `progressGeo` holds whole samples, so its identity changes only when the
    // cursor crosses one, and `progressTip` is the two-point stub to the interpolated head. A single
    // growing LineString would make react-map-gl deep-compare up to 5,360 pairs on every commit.
    const progressGeo = useMemo(() => {
        if (!cursor || !trail?.samples?.length) return null;
        const coordinates = trail.samples
            .slice(0, cursor.sampleIndex + 1)
            .filter((row) => Number.isFinite(row.lat) && Number.isFinite(row.lon))
            .map((row) => [row.lon, row.lat]);
        return coordinates.length > 1 ? { type: 'LineString', coordinates } : null;
    }, [trail, cursor?.sampleIndex]);

    const progressTip = useMemo(() => {
        if (!cursor || !progressGeo) return null;
        const last = progressGeo.coordinates[progressGeo.coordinates.length - 1];
        if (!Number.isFinite(cursor.lon) || !Number.isFinite(cursor.lat)) return null;
        return { type: 'LineString', coordinates: [last, [cursor.lon, cursor.lat]] };
    }, [cursor, progressGeo]);

    const zone = timezone || 'UTC';
    const tripNumbers = useMemo(() => buildTripNumbers(trips), [trips]);

    /** Hovering a dot reads it. Same rows, same wording as clicking — see PointCard. */
    const describePoint = useCallback(({ kind, id }) => {
        const row = findRow({ kind, id });
        return row ? buildFocus(row, kind, zone, tripNumbers.get(row.tripId)) : null;
    }, [findRow, zone, tripNumbers]);
    const selectedTripNo = tripNumbers.get(selectedTrip?.startTraceId);
    const safetyDeckColumns = useMemo(
        () => safetyColumns({ showAsset: false, tripNumbers, zone })
            .filter((column) => SAFETY_DECK_COLUMNS.includes(column.id)),
        [tripNumbers, zone],
    );
    const idleDeckColumns = useMemo(
        () => idleColumns({ showAsset: false, tripNumbers, zone })
            .filter((column) => IDLE_DECK_COLUMNS.includes(column.id)),
        [tripNumbers, zone],
    );

    return (
        <div className="od-map-grid">
            <div className="od-map-rail">
                <TripRail
                  trips={trips}
                  events={OD_TELEMETRY_ENGINES_ENABLED ? events : undefined}
                  selectedTripId={selectedTrip?.startTraceId}
                  onSelectTrip={onSelectTrip}
                  onHoverTrip={onHoverTrip}
                  timezone={timezone}
                  totalCount={totalTripCount}
                />
            </div>

            <div className="od-map-wrap" ref={mapWrapRef}>
                {trips.length === 0 ? (
                    <div className="od-map-empty">
                        <div className="od-empty">
                            <div className="od-empty-title">Nothing to draw</div>
                            <div className="od-empty-text">
                                No trips match the current filters, so the map has no routes to show.
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Top-LEFT: the top-right corner belongs to the legend and the hover
                            readout, which appears and disappears under the cursor and must not
                            shift the controls when it does. */}
                        <div className="od-map-controls">
                            <div className="od-map-control-row">
                                <span className="od-map-control-text">Trip numbers</span>
                                <IOSSwitch checked={showTripNumbers} onChange={onToggleTripNumbers} />
                            </div>
                            <div className="od-map-control-row">
                                <span className="od-map-control-text">Satellite view</span>
                                <IOSSwitch
                                  checked={mapTheme === MAP_THEMES.SATELLITE}
                                  onChange={onToggleMapTheme}
                                />
                            </div>
                            <div className="od-map-control-row">
                                <OutlinedButton
                                  label="Reset view"
                                  width="100%"
                                  onClickFunction={onResetView}
                                  disable={!canResetView}
                                />
                            </div>
                        </div>
                        {/* No key={mapTheme}: remounting on every style switch threw the camera
                            away, and react-map-gl re-adds a Source itself on 'styledata' when the
                            style swap removed it (react-map-gl/dist/esm/components/source.js). */}
                        <MapTripReport
                          trips={trips}
                          selectedTrip={selectedTrip}
                          hoverTripId={hoverTripId}
                          onHoverTrip={onHoverTrip}
                          onSelectTrip={onSelectTrip}
                          showTripNumbers={showTripNumbers}
                          showOdMarkers
                          mapTheme={mapTheme}
                          events={OD_TELEMETRY_ENGINES_ENABLED ? events : undefined}
                          episodes={OD_TELEMETRY_ENGINES_ENABLED ? episodes : undefined}
                          fuelEvents={OD_TELEMETRY_ENGINES_ENABLED ? fuelEvents : undefined}
                          chargeSessions={OD_TELEMETRY_ENGINES_ENABLED ? chargeSessions : undefined}
                          progressGeo={progressGeo}
                          progressTip={progressTip}
                          cursor={cursor}
                          followCursor={follow}
                          focus={focus}
                          onClearFocus={onClearFocus}
                          onPickPoint={onPickPoint}
                          describePoint={describePoint}
                        />
                        <MapLegend mapTheme={mapTheme} />
                        <HoverPanel trips={trips} hoverTripId={hoverTripId} timezone={timezone} />
                    </>
                )}
            </div>

            {/* Trip tape. Reserved slot: a time-based scrubber needs timestamped positions,
                which tripGeo does not carry. Gated on its OWN flag — everything else in this
                view runs on data the analytics endpoint already returns, and sharing one flag
                kept all of it dark waiting for the trail. */}
            {OD_TRIP_TAPE_ENABLED && (
                <div className="od-map-tape">
                    <TripTape
                      trip={selectedTrip}
                      trail={trail}
                      loading={trailLoading}
                      error={trail?.error}
                      notFound={trail?.notFound}
                      tooLarge={trail?.tooLarge}
                      events={events}
                      episodes={episodes}
                      timezone={timezone}
                      follow={follow}
                      onToggleFollow={(event) => setFollow(event.target.checked)}
                      onScrub={setCursor}
                      onUserSeek={onClearFocus}
                    />
                </div>
            )}

            {/* Safety events and Idle, side by side in a two-column grid. Both are the
                prototype's, and they read the same rows as the Safety and Idle tabs. The flag
                gates the whole deck now that these are the only two panels in it — an empty
                grid would otherwise leave a row gap under the tape. */}
            {OD_TELEMETRY_ENGINES_ENABLED && (
                <div className="od-map-deck">
                    <TripScopedPanel
                      title="Safety events"
                      subtitle="Harsh acceleration, braking, cornering and overspeeding"
                      rows={events}
                      columns={safetyDeckColumns}
                      onRowClicked={withMapScroll(onFocusEvent)}
                      selectedTrip={selectedTrip}
                      tripNo={selectedTripNo}
                      scopeToTrip={scopeToTrip}
                      onToggleScope={(event) => setScopeToTrip(event.target.checked)}
                      emptyText={scopeToTrip && selectedTrip
                          ? 'No safety events in this trip.'
                          : 'No safety events in this window.'}
                      note="An empty list is clean driving only when the signal is available — the Safety tab states which."
                    />
                    <TripScopedPanel
                      title="Idle in this trip"
                      subtitle="Stationary with the engine running"
                      rows={episodes}
                      columns={idleDeckColumns}
                      onRowClicked={withMapScroll(onFocusEpisode)}
                      selectedTrip={selectedTrip}
                      tripNo={selectedTripNo}
                      scopeToTrip={scopeToTrip}
                      onToggleScope={(event) => setScopeToTrip(event.target.checked)}
                      emptyText={scopeToTrip && selectedTrip
                          ? 'No qualifying idle in this trip.'
                          : 'No qualifying idle in this window.'}
                      note="Episodes shorter than the agency's minimum never become rows — see the Idle tab for the window totals."
                    />
                </div>
            )}
        </div>
    );
};

export default RouteMapView;
